import { useState, useRef } from "react";
import { UploadCloud, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { uploadOcoren } from "@/lib/ocorenApi";

export const UploadZone = ({ onUploaded, compact = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]);
  const inputRef = useRef(null);

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList).filter((f) =>
      /\.(txt|edi)$/i.test(f.name) || f.type === "text/plain" || f.type === ""
    );
    if (arr.length === 0) {
      toast.error("Selecione arquivos .TXT EDI OCOREN válidos");
      return;
    }
    setSelected(arr);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const submit = async () => {
    if (selected.length === 0) return;
    setUploading(true);
    try {
      const res = await uploadOcoren(selected);
      const total = (res.arquivos_processados || []).reduce(
        (acc, a) => acc + (a.total_movimentacoes || 0),
        0
      );
      const novas = (res.arquivos_processados || []).reduce(
        (acc, a) => acc + (a.diff_resumo?.novas_nfs || 0),
        0
      );
      const alteradas = (res.arquivos_processados || []).reduce(
        (acc, a) => acc + (a.diff_resumo?.status_alterado || 0) + (a.diff_resumo?.regressoes || 0),
        0
      );
      const parts = [
        `${selected.length} arquivo(s) processado(s) — ${total} eventos`,
      ];
      if (novas > 0) parts.push(`${novas} NF(s) nova(s)`);
      if (alteradas > 0) parts.push(`${alteradas} status alterado(s)`);
      toast.success(parts.join(" • "));
      setSelected([]);
      if (inputRef.current) inputRef.current.value = "";
      onUploaded?.(res);
    } catch (err) {
      toast.error("Falha ao processar arquivo(s)");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const remove = (idx) => {
    setSelected((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid="upload-zone-container" className="w-full">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="upload-dropzone"
        className={`border-2 border-dashed rounded-md ${
          compact ? "p-6" : "p-10"
        } flex flex-col items-center justify-center cursor-pointer transition-all text-center ${
          isDragging
            ? "border-[#09090B] bg-[#FAFAFA]"
            : "border-[#E4E4E7] bg-white hover:border-[#09090B] hover:bg-[#FAFAFA]"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".txt,.TXT,.edi"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="upload-file-input"
        />
        <UploadCloud
          size={compact ? 28 : 36}
          strokeWidth={1.5}
          className="text-[#09090B] mb-3"
        />
        <div className="font-heading font-semibold text-[#09090B] text-base">
          Arraste arquivos .TXT EDI OCOREN aqui
        </div>
        <div className="text-xs text-[#52525B] mt-1">
          ou clique para selecionar — formatos 340 / 540
        </div>
      </div>

      {selected.length > 0 && (
        <div className="mt-4 border border-[#E4E4E7] rounded-md bg-white">
          <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.15em] text-[#52525B] font-semibold">
              {selected.length} arquivo(s) selecionado(s)
            </div>
            <button
              data-testid="upload-submit-btn"
              onClick={submit}
              disabled={uploading}
              className="bg-[#09090B] text-white hover:bg-[#27272A] disabled:opacity-60 px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Processando…
                </>
              ) : (
                <>Processar arquivos</>
              )}
            </button>
          </div>
          <ul className="divide-y divide-[#E4E4E7]">
            {selected.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="px-4 py-2.5 flex items-center justify-between text-sm"
                data-testid={`upload-file-item-${i}`}
              >
                <div className="flex items-center gap-3 truncate">
                  <FileText size={14} className="text-[#52525B] shrink-0" />
                  <span className="truncate text-[#09090B]">{f.name}</span>
                  <span className="text-xs text-[#52525B] font-mono-data">
                    {(f.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <button
                  onClick={() => remove(i)}
                  className="text-[#52525B] hover:text-[#F43F5E] transition-colors p-1"
                  data-testid={`upload-remove-${i}`}
                  aria-label="Remover"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadZone;
